import * as Icons from '@ant-design/icons';
import { Button, Dropdown, MenuProps, Space, Tag } from 'antd';
import { PageHeader } from 'components/professor_area/PageHeader';
import { CERTIFICATE_STATUS_MAP } from 'constants/certificate-status-map';
import useNotification from 'hooks/useNotification';
import { useCallback, useMemo } from 'react';
import { handleDownloadDeclaration } from 'services/report';
import { useNotification as useCoreNotification } from '@refinedev/core';
import { CertificateStatusValidator } from 'services/certificate';
import { useCertificateStore } from '../../certificate.store';

export const CertificatePageHeaderShow = () => {
  const {
    certificate,
    enrollment,
    hasCertificate,
    canIssueCertificate,
    openApprovesDrawer,
    openNewCertificateModal,
    openCreateCertificateModal,
    openEmissionsDrawer,
    downloadCertificate: downloadCertificateAction
  } = useCertificateStore();

  const { showError } = useNotification();
  const { open } = useCoreNotification();

  const handleDownloadCertificate = useCallback(async () => {
    if (!certificate) return;

    try {
      await downloadCertificateAction(certificate);
    } catch (error) {
      showError('Erro ao baixar certificado. Tente novamente.');
    }
  }, [certificate, downloadCertificateAction, showError]);

  const handleDownloadAcademicRecord = useCallback(async () => {
    if (!enrollment?.id) {
      showError('Matrícula não encontrada para emitir o histórico.');
      return;
    }
    await handleDownloadDeclaration(enrollment.id, open!);
  }, [open, enrollment?.id, showError]);

  const dropdownItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = [];

    items.push({
      key: 'download_academic_record',
      icon: <Icons.FileTextOutlined />,
      label: 'Baixar histórico escolar',
      onClick: handleDownloadAcademicRecord
    });

    if (hasCertificate && certificate) {
      items.push({
        key: 'download',
        icon: <Icons.DownloadOutlined />,
        label: 'Baixar certificado',
        onClick: handleDownloadCertificate
      });
    }

    if (enrollment?.has_certificate) {
      items.push({
        key: 'history',
        icon: <Icons.HistoryOutlined />,
        onClick: openEmissionsDrawer,
        label: 'Histórico de emissões'
      });
    }

    return items;
  }, [
    enrollment,
    hasCertificate,
    certificate,
    handleDownloadCertificate,
    openEmissionsDrawer
  ]);

  const showApprovesButton =
    enrollment &&
    !CertificateStatusValidator.isPending(enrollment.certificate_status);

  const showDropdown = hasCertificate || enrollment?.has_certificate;

  return (
    <PageHeader
      title={enrollment?.user.full_name_or_name || 'Visualizar Certificado'}
      icon={Icons.AuditOutlined}
      actions={
        <Space wrap size={12}>
          {/* Aprovações button */}
          {showApprovesButton && (
            <Button
              icon={<Icons.SafetyOutlined />}
              onClick={openApprovesDrawer}
            >
              Aprovações
            </Button>
          )}

          {/* Novo certificado button */}
          {hasCertificate && (
            <Button
              icon={<Icons.PlusOutlined />}
              onClick={openNewCertificateModal}
            >
              Novo certificado
            </Button>
          )}

          {/* Emitir certificado button */}
          {canIssueCertificate && (
            <Button
              icon={<Icons.PlusOutlined />}
              type="primary"
              onClick={openCreateCertificateModal}
            >
              Emitir certificado
            </Button>
          )}

          {/* Actions dropdown */}
          {showDropdown && (
            <Dropdown
              menu={{ items: dropdownItems }}
              trigger={['click']}
              disabled={dropdownItems.length === 0}
            >
              <Button icon={<Icons.EllipsisOutlined />} />
            </Dropdown>
          )}
        </Space>
      }
      meta={
        enrollment && (
          <Space wrap>
            <Tag
              className="mr-0"
              color={
                CERTIFICATE_STATUS_MAP[
                  enrollment.certificate_status ?? 'pending'
                ].color
              }
            >
              {
                CERTIFICATE_STATUS_MAP[
                  enrollment.certificate_status ?? 'pending'
                ].text
              }
            </Tag>
            <Tag className="mr-0">Curso: {enrollment.course.name}</Tag>
            <Tag className="mr-0">Matrícula: {enrollment.registration}</Tag>
          </Space>
        )
      }
    />
  );
};
